import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/shallow';
import { useTargets } from './useTargets';
import { useAutoSelectCompany } from './useAutoSelectCompany';
import {
  useProjectStore,
  type PortalTarget,
} from '../store/projectStore';
import { useMainContentTabs } from '../store/layoutStore';
import { isAppTarget } from '../utils/target-scope.utils';

export function shouldAutoSelectTarget({
  isCompanyReady,
  isLoadingTargets,
  targets,
  targetId,
}: {
  isCompanyReady: boolean;
  isLoadingTargets: boolean;
  targets: PortalTarget[];
  targetId: string;
}): boolean {
  if (!isCompanyReady || isLoadingTargets || targetId || targets.length === 0) {
    return false;
  }

  return true;
}

export function resolveAutoSelectTarget({
  targets,
}: {
  targets: PortalTarget[];
}): PortalTarget | null {
  return targets[0] ?? null;
}

export function useAutoSelectTarget() {
  const tLayout = useTranslations('layout');
  const { isCompanyReady } = useAutoSelectCompany();
  const { companyId, targetId, setTarget } = useProjectStore(
    useShallow((state) => ({
      companyId: state.companyId,
      targetId: state.targetId,
      setTarget: state.setTarget,
    })),
  );
  const { targets, isLoading } = useTargets({ companyId });
  const { openTab } = useMainContentTabs();
  const lastAutoSelectedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !shouldAutoSelectTarget({
        isCompanyReady,
        isLoadingTargets: isLoading,
        targets,
        targetId,
      })
    ) {
      return;
    }

    const nextTarget = resolveAutoSelectTarget({ targets });

    if (!nextTarget) {
      return;
    }

    const selectionKey = `${companyId}:${nextTarget.id}:${nextTarget.targetType}`;

    if (lastAutoSelectedKeyRef.current === selectionKey) {
      return;
    }

    lastAutoSelectedKeyRef.current = selectionKey;
    setTarget(nextTarget);
    openTab({
      id: 'application-overview',
      title: isAppTarget(nextTarget.targetType)
        ? tLayout('home')
        : tLayout('targetOverview'),
      type: 'application',
    });
  }, [
    companyId,
    isCompanyReady,
    isLoading,
    openTab,
    setTarget,
    tLayout,
    targetId,
    targets,
  ]);
}
